import { FC, useState } from 'react'
import { Box, InputBase, Stack, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'

import { Layout } from '@/apps/latest/components/templates/Layout'
import { type HomecareCandidate } from '@/apps/latest/features/home-visit/homecareApi'
import { formatBirthDate } from '@/apps/latest/features/home-visit/printHomeVisitReport'
import {
  useHomecareCandidates,
  useRegisterHomecarePatient,
} from '@/apps/latest/features/home-visit/useHomecare'
import { searchInputSx } from '@/apps/latest/shared/styles/inlineInput'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { BodyText } from '@/components/atoms/BodyText'
import { MetaText } from '@/components/atoms/MetaText'
import { useToast } from '@/shared/contexts/ToastContext'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { RADIUS } from '@/shared/styles/radii'
import { FONT_WEIGHT, scaledFontSize } from '@/shared/styles/typography'

const rowSx = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 1.5,
  width: '100%',
  px: 1.25,
  py: 1,
  borderRadius: `${RADIUS.sm}px`,
  textAlign: 'left',
  cursor: 'pointer',
  '&:hover': { backgroundColor: colorTokens.overlay.hover },
} as const

// 在宅 (/homecare): 店の患者を探して選び、在宅の患者に登録する。一覧は左ペインが持つので、
// 本文は検索の 1 欄と結果の行だけ。患者は処方でレセコンに入っているので、手で打つ登録は持たない。
// 介護度と宛先は登録した後の患者のページで入れる (登録の手数を増やさない)。
const HomecareHomePage: FC = () => {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [query, setQuery] = useState('')
  const debounced = useDebounce(query, 250)
  const { data: candidates, isFetching } = useHomecareCandidates(debounced)
  const register = useRegisterHomecarePatient()

  const choose = async (candidate: HomecareCandidate) => {
    if (candidate.home_care_patient_id) {
      navigate(`/homecare/patients/${candidate.home_care_patient_id}`)
      return
    }
    try {
      const patient = await register.mutateAsync(candidate.id)
      navigate(`/homecare/patients/${patient.id}`)
    } catch {
      showToast('在宅の患者を登録できませんでした', 'error')
    }
  }

  const searched = debounced.trim() !== '' && candidates !== undefined
  const rows = searched ? candidates : []

  return (
    <Layout breadcrumb={['在宅', '在宅の患者を登録']}>
      <Typography
        component="p"
        sx={{
          mb: 2,
          fontSize: scaledFontSize('bodySm'),
          color: colorTokens.fg.muted,
        }}
      >
        在宅で訪問する患者を選ぶと、左の一覧に出ます。訪問の記録と報告書は患者のページから作ります。
      </Typography>
      <Box sx={{ maxWidth: 640 }}>
        <InputBase
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          fullWidth
          autoFocus
          placeholder="氏名・カナ・患者番号で探す"
          inputProps={{ 'aria-label': '患者を探す' }}
          sx={{ ...searchInputSx, mb: 1 }}
        />
        <Stack spacing={0}>
          {rows.map((candidate) => (
            <Box
              key={candidate.id}
              role="button"
              tabIndex={0}
              aria-disabled={register.isPending}
              onClick={() => !register.isPending && void choose(candidate)}
              onKeyDown={(e) =>
                e.key === 'Enter' &&
                !register.isPending &&
                void choose(candidate)
              }
              sx={rowSx}
            >
              <BodyText sx={{ fontWeight: FONT_WEIGHT.medium }}>
                {candidate.name}
              </BodyText>
              <MetaText>{candidate.name_kana}</MetaText>
              <Box sx={{ flex: 1 }} />
              <MetaText sx={{ color: colorTokens.fg.faint }}>
                {[
                  candidate.birth_date && formatBirthDate(candidate.birth_date),
                  candidate.patient_code,
                ]
                  .filter(Boolean)
                  .join('  ')}
              </MetaText>
              {candidate.home_care_patient_id && <MetaText>登録済み</MetaText>}
            </Box>
          ))}
          {searched && rows.length === 0 && !isFetching && (
            <MetaText sx={{ px: 1.25, py: 1 }}>見つかりません</MetaText>
          )}
        </Stack>
      </Box>
    </Layout>
  )
}

export default HomecareHomePage
